import React, { useState } from 'react';
import axios from 'axios';
import { Bell, Check, Loader2 } from 'lucide-react';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Inline "Notify me when back in stock" capture used inside out-of-stock
 * product cards. Three visual states:
 *   collapsed → button only
 *   open      → email + optional phone + submit
 *   done      → confirmation chip
 *
 * Either email OR phone (or both) must be provided. Phone subscribers
 * receive an SMS via D7 Networks when the admin restocks the product
 * (gated by Admin → Settings → Notifications · SMS).
 *
 * defaultEmail is wired so signed-in customers get their address prefilled.
 */
export const NotifyWhenInStock = ({ productId, defaultEmail = '', testId }) => {
  const [stage, setStage] = useState('collapsed'); // collapsed | open | submitting | done | error
  const [email, setEmail] = useState(defaultEmail);
  const [phone, setPhone] = useState('');
  const [lang, setLang] = useState('en'); // 'en' | 'ar' — controls the SMS template language
  const [errorMsg, setErrorMsg] = useState('');

  const submit = async (e) => {
    e?.preventDefault?.();
    const emailVal = email.trim();
    const phoneVal = phone.trim();
    if (!emailVal && !phoneVal) {
      setErrorMsg('Enter an email or a phone number');
      return;
    }
    if (emailVal && !/^\S+@\S+\.\S+$/.test(emailVal)) {
      setErrorMsg('Enter a valid email');
      return;
    }
    if (phoneVal && phoneVal.replace(/\D/g, '').length < 7) {
      setErrorMsg('Enter a valid phone number');
      return;
    }
    setStage('submitting');
    setErrorMsg('');
    try {
      await axios.post(`${API}/products/${productId}/notify-when-in-stock`, {
        email: emailVal || null,
        phone: phoneVal || null,
        language: lang,
      });
      setStage('done');
    } catch (err) {
      setStage('open');
      setErrorMsg(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || 'Could not subscribe — try again');
    }
  };

  if (stage === 'done') {
    return (
      <div
        className="mt-3 flex items-center justify-center gap-1.5 h-10 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold uppercase tracking-wider"
        data-testid={testId ? `${testId}-done` : `notify-done-${productId}`}
      >
        <Check className="w-3.5 h-3.5" />
        We&apos;ll let you know
      </div>
    );
  }

  if (stage === 'collapsed') {
    return (
      <button
        type="button"
        onClick={() => setStage('open')}
        className="mt-3 flex items-center justify-center gap-1.5 h-10 rounded-md bg-[#EFF6FC] hover:bg-[#DEECF9] border border-[#BBDCF7] text-[#0E4A7B] text-xs font-bold uppercase tracking-wider transition-colors"
        data-testid={testId ? `${testId}-trigger` : `notify-trigger-${productId}`}
      >
        <Bell className="w-3.5 h-3.5" />
        Notify me when back
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="mt-3 space-y-1.5">
      <div className="flex items-center gap-1 mb-1" data-testid={`notify-lang-${productId}`}>
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mr-1">SMS language:</span>
        {[
          { code: 'en', label: 'EN' },
          { code: 'ar', label: 'عربي' },
        ].map((opt) => (
          <button
            key={opt.code}
            type="button"
            onClick={() => setLang(opt.code)}
            className={`h-6 px-2 text-[10px] font-bold uppercase tracking-wider rounded-md transition-colors ${
              lang === opt.code
                ? 'bg-[#0078D4] text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid={`notify-lang-${productId}-${opt.code}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <Input
        type="email"
        autoFocus
        placeholder="your@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="h-9 text-xs"
        data-testid={testId ? `${testId}-input` : `notify-input-${productId}`}
      />
      <div className="flex items-stretch gap-1.5">
        <Input
          type="tel"
          placeholder="+968 9XXX XXXX (SMS, optional)"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="h-9 text-xs flex-1 min-w-0"
          data-testid={testId ? `${testId}-phone` : `notify-phone-${productId}`}
        />
        <button
          type="submit"
          disabled={stage === 'submitting'}
          className="h-9 px-3 rounded-md bg-[#0078D4] hover:bg-[#106EBE] text-white text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-60"
          data-testid={testId ? `${testId}-submit` : `notify-submit-${productId}`}
        >
          {stage === 'submitting' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Send'}
        </button>
      </div>
      {errorMsg && (
        <div className="text-[10px] text-red-600 font-medium" data-testid={`notify-error-${productId}`}>
          {errorMsg}
        </div>
      )}
    </form>
  );
};

export default NotifyWhenInStock;
